import { useMemo } from "react";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createPracticeViewerAccess,
  deletePracticeViewerAccess,
  getPracticeViewerAccess,
  updatePracticeViewerAccess,
} from "apps/project-iq/api";
import {
  IPracticeViewerAccess,
  IPracticeViewerAccessInput,
} from "apps/project-iq/models/IPracticeViewerAccess";

export const PRACTICE_VIEWER_ACCESS_QUERY_KEY = "practiceViewerAccess";

export interface IUsePracticeViewerAccessResult {
  practiceViewerAccessList: IPracticeViewerAccess[];
  isLoading: boolean;
  isError: boolean;
  createMutation: UseMutationResult<
    IPracticeViewerAccess,
    Error,
    IPracticeViewerAccessInput
  >;
  updateMutation: UseMutationResult<
    IPracticeViewerAccess,
    Error,
    IPracticeViewerAccess
  >;
  deleteMutation: UseMutationResult<unknown, Error, string>;
}

export const usePracticeViewerAccess = (): IUsePracticeViewerAccessResult => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<IPracticeViewerAccess[], Error>(
    {
      queryKey: [PRACTICE_VIEWER_ACCESS_QUERY_KEY],
      queryFn: () => getPracticeViewerAccess(),
    },
  );

  const practiceViewerAccessList = useMemo(() => data ?? [], [data]);

  const createMutation = useMutation<
    IPracticeViewerAccess,
    Error,
    IPracticeViewerAccessInput
  >((input: IPracticeViewerAccessInput) => createPracticeViewerAccess(input), {
    onSuccess: () => {
      queryClient.invalidateQueries([PRACTICE_VIEWER_ACCESS_QUERY_KEY]);
    },
  });

  const updateMutation = useMutation<
    IPracticeViewerAccess,
    Error,
    IPracticeViewerAccess
  >(
    ({ id, user, practiceIds }: IPracticeViewerAccess) =>
      updatePracticeViewerAccess(id, { user, practiceIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PRACTICE_VIEWER_ACCESS_QUERY_KEY]);
      },
    },
  );

  const deleteMutation = useMutation<unknown, Error, string>(
    (id: string) => deletePracticeViewerAccess(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PRACTICE_VIEWER_ACCESS_QUERY_KEY]);
      },
    },
  );

  return {
    practiceViewerAccessList,
    isLoading,
    isError,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
